// ===========================================
import { useTranslation } from 'react-i18next';
// 🧾 COMPONENTE DE CUENTAS MODERNO: src/components/AGTFin/dashboard/bills/FinBill.js
// ===========================================

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Alert,
  Grid,
  Paper,
  Divider,
  Switch,
  FormControlLabel,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import {
  Receipt as ReceiptIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Warning as WarningIcon,
  AccessTime as AccessTimeIcon,
  CalendarToday as CalendarTodayIcon,
  AttachMoney as AttachMoneyIcon,
  FilterList as FilterListIcon,
  Visibility as VisibilityIcon,
  TrendingDown as TrendingDownIcon,
  AccountBalance as AccountBalanceIcon,
  Analytics as AnalyticsIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import { format } from 'date-fns';
import agtFinService from '../../services/agtFinService';

// ===========================================
// 🎬 VARIANTES DE ANIMACIÓN TIPO macOS
// ===========================================
const containerVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94],
      staggerChildren: 0.08
    }
  }
};

const itemVariants = {
  hidden: {
    opacity: 0,
    y: 15
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

const parseLocalDate = (dateString) => {
  if (!dateString || typeof dateString !== 'string') {
    return null;
  }

  const [year, month, day] = dateString.split('-').map(Number);
  if (!year || !month || !day) {
    return null;
  }

  return new Date(year, month - 1, day);
};

const normalizeDateInput = (value) => {
  if (value instanceof Date && !isNaN(value.getTime())) {
    return value;
  }

  if (typeof value === 'string') {
    return parseLocalDate(value);
  }

  return null;
};

const formatLocalDateString = (dateObj) => {
  if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) {
    return null;
  }

  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// ===========================================
// 🎨 PALETA DE COLORES EN ESCALA DE GRISES MODERNA
// ===========================================
const getGrayscalePalette = (theme) => ({
  // Colores principales en escala de grises
  primary: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
  secondary: theme.palette.mode === 'dark' ? '#E0E0E0' : '#424242',
  tertiary: theme.palette.mode === 'dark' ? '#BDBDBD' : '#616161',
  quaternary: theme.palette.mode === 'dark' ? '#9E9E9E' : '#757575',
  
  // Gradientes para elementos visuales
  gradients: {
    primary: theme.palette.mode === 'dark' 
      ? 'linear-gradient(180deg, #FFFFFF 0%, #E0E0E0 100%)'
      : 'linear-gradient(180deg, #000000 0%, #424242 100%)',
    
    secondary: theme.palette.mode === 'dark'
      ? 'linear-gradient(180deg, #E0E0E0 0%, #BDBDBD 100%)'
      : 'linear-gradient(180deg, #424242 0%, #616161 100%)',
    
    tertiary: theme.palette.mode === 'dark'
      ? 'linear-gradient(180deg, #BDBDBD 0%, #9E9E9E 100%)'
      : 'linear-gradient(180deg, #616161 0%, #757575 100%)',
    
    card: theme.palette.mode === 'dark'
      ? 'linear-gradient(145deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)'
      : 'linear-gradient(145deg, rgba(255,255,255,1) 0%, rgba(248,249,250,1) 100%)'
  },
  
  // Colores para estados de las cuentas
  status: {
    pending: theme.palette.mode === 'dark' ? '#BDBDBD' : '#616161',
    paid: theme.palette.mode === 'dark' ? '#E0E0E0' : '#424242',
    overdue: theme.palette.mode === 'dark' ? '#9E9E9E' : '#757575'
  },
  
  // Opacidades para diferentes elementos
  backgrounds: {
    light: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
    medium: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.04)',
    dark: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.06)',
    card: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.9)'
  }
});

// ===========================================
// 🎯 COMPONENTES ESTILIZADOS MODERNOS
// ===========================================

// Contenedor principal moderno
const ModernContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: 24,
  width: '100%',
  maxWidth: 1200,
  margin: '0 auto',
  background: theme.palette.mode === 'dark' 
    ? 'rgba(0,0,0,0.2)' 
    : 'rgba(255,255,255,0.8)',
  backdropFilter: 'blur(20px)',
  border: theme.palette.mode === 'dark' 
    ? '1px solid rgba(255,255,255,0.1)' 
    : '1px solid rgba(0,0,0,0.05)',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    borderRadius: 16,
  }
}));

// Tarjeta de resumen moderna
const SummaryCard = styled(Card)(({ theme }) => {
  const colors = getGrayscalePalette(theme);
  return {
    borderRadius: 20,
    background: colors.backgrounds.card,
    backdropFilter: 'blur(20px)',
    border: theme.palette.mode === 'dark' 
      ? '1px solid rgba(255,255,255,0.1)' 
      : '1px solid rgba(0,0,0,0.05)',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 8px 32px rgba(0,0,0,0.3)'
      : '0 8px 32px rgba(0,0,0,0.1)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    overflow: 'hidden',
    position: 'relative',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.palette.mode === 'dark'
        ? '0 12px 40px rgba(0,0,0,0.4)'
        : '0 12px 40px rgba(0,0,0,0.15)',
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 4,
      background: colors.gradients.primary,
    }
  };
});

// Tarjeta de cuenta moderna
const ModernBillCard = styled(Card)(({ theme, status }) => {
  const colors = getGrayscalePalette(theme);
  
  const getStatusGradient = () => {
    switch (status) {
      case 'paid':
        return colors.gradients.primary;
      case 'pending':
        return colors.gradients.secondary;
      case 'overdue':
        return colors.gradients.tertiary;
      default:
        return colors.gradients.primary;
    }
  };

  return {
    borderRadius: 20,
    background: colors.backgrounds.card,
    backdropFilter: 'blur(20px)',
    border: theme.palette.mode === 'dark' 
      ? '1px solid rgba(255,255,255,0.1)' 
      : '1px solid rgba(0,0,0,0.05)',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 8px 32px rgba(0,0,0,0.2)'
      : '0 8px 32px rgba(0,0,0,0.08)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    overflow: 'hidden',
    position: 'relative',
    marginBottom: theme.spacing(2),
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.palette.mode === 'dark'
        ? '0 12px 40px rgba(0,0,0,0.3)'
        : '0 12px 40px rgba(0,0,0,0.12)',
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 4,
      background: getStatusGradient(),
    }
  };
});

// Botón moderno con estilo del dashboard
const ModernButton = styled(Button)(({ theme }) => {
  const colors = getGrayscalePalette(theme);
  return {
    borderRadius: 16,
    padding: '12px 24px',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '14px',
    background: colors.gradients.primary,
    color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF',
    border: 'none',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 4px 16px rgba(255,255,255,0.1)'
      : '0 4px 16px rgba(0,0,0,0.1)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: theme.palette.mode === 'dark'
        ? '0 8px 24px rgba(255,255,255,0.15)'
        : '0 8px 24px rgba(0,0,0,0.15)',
      background: colors.gradients.secondary,
    },
    '&:active': {
      transform: 'translateY(0px)',
    },
    '&:disabled': {
      opacity: 0.6,
      transform: 'none',
      boxShadow: 'none',
    }
  };
});

// Diálogo moderno
const ModernDialog = styled(Dialog)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    '& .MuiDialog-paper': {
      borderRadius: 24,
      background: isWallpaperMode
        ? 'rgba(128, 128, 128, 0.3)'
        : (theme.palette.mode === 'dark'
          ? 'rgba(30,30,30,0.95)'
          : 'rgba(255,255,255,0.95)'),
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      border: isWallpaperMode
        ? '1px solid rgba(255, 255, 255, 0.2)'
        : (theme.palette.mode === 'dark'
          ? '1px solid rgba(255,255,255,0.1)'
          : '1px solid rgba(0,0,0,0.05)'),
      boxShadow: 'none',
    }
  };
});

// Campo de texto moderno
const ModernTextField = styled(TextField)(({ theme }) => {
  const colors = getGrayscalePalette(theme);
  return {
    '& .MuiOutlinedInput-root': {
      borderRadius: 12,
      background: colors.backgrounds.light,
      transition: 'all 0.3s ease',
      '&:hover': {
        background: colors.backgrounds.medium,
      },
      '&.Mui-focused': {
        background: colors.backgrounds.medium,
        '& fieldset': {
          borderColor: colors.primary,
          borderWidth: 2,
        }
      },
      '& fieldset': {
        borderColor: colors.backgrounds.dark,
        transition: 'all 0.3s ease',
      }
    },
    '& .MuiInputLabel-root': {
      fontWeight: 500,
    }
  };
});

// FormControl moderno
const ModernFormControl = styled(FormControl)(({ theme }) => {
  const colors = getGrayscalePalette(theme);
  return {
    '& .MuiOutlinedInput-root': {
      borderRadius: 12,
      background: colors.backgrounds.light,
      transition: 'all 0.3s ease',
      '&:hover': {
        background: colors.backgrounds.medium,
      },
      '&.Mui-focused': {
        background: colors.backgrounds.medium,
        '& fieldset': {
          borderColor: colors.primary,
          borderWidth: 2,
        }
      },
      '& fieldset': {
        borderColor: colors.backgrounds.dark,
        transition: 'all 0.3s ease',
      }
    },
    '& .MuiInputLabel-root': {
      fontWeight: 500,
    }
  };
});

// Chip moderno con gradiente
const ModernChip = styled(Chip)(({ theme, chiptype }) => {
  const colors = getGrayscalePalette(theme);
  
  const getChipStyles = () => {
    switch (chiptype) {
      case 'paid':
        return {
          background: colors.gradients.primary,
          color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF',
        };
      case 'pending':
        return {
          background: colors.gradients.secondary,
          color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF',
        };
      case 'overdue':
        return {
          background: colors.gradients.tertiary,
          color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF',
        };
      default:
        return {
          background: colors.gradients.primary,
          color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF',
        };
    }
  };

  return {
    borderRadius: 12,
    fontWeight: 600,
    fontSize: '12px',
    height: 28,
    textTransform: 'uppercase',
    ...getChipStyles(),
    '& .MuiChip-icon': {
      color: 'inherit',
    }
  };
});

// ===========================================
// 📊 SECCIÓN DE RESUMEN MEJORADA
// ===========================================
const SummarySection = ({ summary, userProfile, theme }) => {
  const { t } = useTranslation();
  const colors = getGrayscalePalette(theme);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const formatCurrency = (amount) => {
    return agtFinService.formatCurrency(amount || 0, userProfile?.currency || 'USD');
  };

  const summaryItems = [
    {
      title: t('agtfin.bills.pending'),
      value: summary?.pending?.total || 0,
      count: summary?.pending?.count || 0,
      icon: <AccessTimeIcon />,
      gradient: colors.gradients.secondary,
      status: 'pending'
    },
    {
      title: t('agtfin.bills.paid'),
      value: summary?.paid?.total || 0,
      count: summary?.paid?.count || 0,
      icon: <CheckIcon />,
      gradient: colors.gradients.primary,
      status: 'paid'
    },
    {
      title: t('agtfin.bills.overdue'),
      value: summary?.overdue?.total || 0,
      count: summary?.overdue?.count || 0,
      icon: <WarningIcon />,
      gradient: colors.gradients.tertiary,
      status: 'overdue'
    }
  ];

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 3, color: colors.primary }}>
        {t('agtfin.bills.summary')}
      </Typography>
      
      <Grid container spacing={isMobile ? 2 : 3}>
        {summaryItems.map((item, index) => (
          <Grid item xs={12} md={4} key={index}>
            <SummaryCard>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2 }}>
                  <Box sx={{
                    p: 2,
                    borderRadius: '16px',
                    background: item.gradient,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {React.cloneElement(item.icon, { 
                      sx: { 
                        fontSize: 28, 
                        color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF' 
                      } 
                    })}
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" color="text.secondary" fontWeight={500} sx={{ mb: 0.5 }}>
                      {item.title}
                    </Typography>
                    <Typography variant="h4" component="div" fontWeight={700} sx={{ color: colors.primary }}>
                      {formatCurrency(item.value)}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  {item.count} {item.count === 1 ? t('agtfin.bills.bill') : t('agtfin.bills.bills_plural')}
                </Typography>
              </CardContent>
            </SummaryCard>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

// Tipos de factura - Las etiquetas se obtienen mediante traducción en tiempo de ejecución
const getBillTypes = (t) => [
  { value: 'utilities', label: t('agtfin.bills.types.utilities'), icon: <AccountBalanceIcon /> },
  { value: 'rent', label: t('agtfin.bills.types.rent'), icon: <AccountBalanceIcon /> },
  { value: 'insurance', label: t('agtfin.bills.types.insurance'), icon: <AccountBalanceIcon /> },
  { value: 'subscription', label: t('agtfin.bills.types.subscription'), icon: <ReceiptIcon /> },
  { value: 'loan', label: t('agtfin.bills.types.loan'), icon: <TrendingDownIcon /> },
  { value: 'credit_card', label: t('agtfin.bills.types.credit_card'), icon: <AttachMoneyIcon /> },
  { value: 'other', label: t('agtfin.bills.types.other'), icon: <ReceiptIcon /> }
];

// ===========================================
// 🧾 COMPONENTE PRINCIPAL MODERNO
// ===========================================
const FinBill = ({ user, userProfile }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const colors = getGrayscalePalette(theme);
  const billTypes = getBillTypes(t);

  // Estados principales
  const [bills, setBills] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Estados de modales
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  
  // Estados de filtrado
  const [filterStatus, setFilterStatus] = useState('all');
  const [showOverdueOnly, setShowOverdueOnly] = useState(false);
  
  // Estados del formulario
  const [formData, setFormData] = useState({
    name: '',
    amount: '',
    dueDate: new Date(),
    billType: 'utilities',
    category: '',
    description: '',
    isRecurring: false,
    frequency: 'monthly'
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);

  // Cargar facturas y resumen
  const loadBillsData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('🔄 Cargando datos de facturas para usuario:', user?.uid);
      
      if (!agtFinService || !agtFinService.apiClient) {
        throw new Error(t('agtfin.bills.messages.serviceNotAvailable'));
      }
      
      const [billsResponse, summaryResponse] = await Promise.all([
        agtFinService.getBills(),
        agtFinService.getBillsSummary()
      ]);
      
      console.log('✅ Datos de facturas recibidos:', { billsResponse, summaryResponse });
      
      setBills(billsResponse.bills || []);
      setSummary(summaryResponse.summary || null);
      
    } catch (err) {
      console.error('❌ Error cargando datos de facturas:', err);

      let errorMessage = t('agtfin.bills.messages.loadError');

      if (err.message.includes('apiClient')) {
        errorMessage = t('agtfin.bills.messages.serviceError');
      } else if (err.response?.status === 401) {
        errorMessage = t('agtfin.bills.messages.sessionExpired');
      } else if (err.userMessage) {
        errorMessage = err.userMessage;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Cargar datos iniciales
  useEffect(() => {
    if (user?.uid) {
      loadBillsData();
    } else {
      setLoading(false);
      setError(t('agtfin.bills.messages.userNotAvailable'));
    }
  }, [user, loadBillsData]);

  // Validar formulario
  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = t('agtfin.common.requiredField');
    }

    if (!formData.amount) {
      errors.amount = t('agtfin.common.requiredField');
    } else if (isNaN(formData.amount) || parseFloat(formData.amount) <= 0) {
      errors.amount = t('agtfin.common.positiveNumberRequired');
    }

    if (!formData.dueDate) {
      errors.dueDate = t('agtfin.common.requiredField');
    }

    return errors;
  };

  // Crear nueva factura
  const handleCreateBill = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      setSubmitLoading(true);
      setError('');
      
      let formattedDate;
      try {
        const dateObj = normalizeDateInput(formData.dueDate);
        if (!dateObj) {
          throw new Error(t('agtfin.bills.messages.invalidDate'));
        }
        formattedDate = formatLocalDateString(dateObj);
      } catch (dateError) {
        setFormErrors({ dueDate: t('agtfin.bills.messages.invalidDateFormat') });
        return;
      }
      
      const billData = {
        name: formData.name.trim(),
        amount: parseFloat(formData.amount),
        dueDate: formattedDate,
        billType: formData.billType,
        category: formData.category?.trim() || '',
        description: formData.description?.trim() || '',
        isRecurring: Boolean(formData.isRecurring),
        frequency: formData.frequency
      };
      
      console.log('💾 Creando nueva factura:', billData);
      
      await agtFinService.createBill(billData);
      
      // Recargar datos
      await loadBillsData();
      
      // Limpiar formulario y cerrar modal
      resetForm();
      setOpenCreateModal(false);
      
      console.log('✅ Factura creada exitosamente');
      
    } catch (err) {
      console.error('❌ Error creando factura:', err);
      
      let errorMessage = 'No se pudo crear la cuenta.';
      
      if (err.response?.status === 400) {
        errorMessage = err.response.data?.error || 'Datos inválidos. Verifica la información.';
      } else if (err.response?.status === 401) {
        errorMessage = 'Sesión expirada. Por favor, inicia sesión nuevamente.';
      } else if (err.userMessage) {
        errorMessage = err.userMessage;
      }
      
      setError(errorMessage);
    } finally {
      setSubmitLoading(false);
    }
  };

  // Actualizar factura
  const handleUpdateBill = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      setSubmitLoading(true);
      setError('');
      
      let formattedDate;
      try {
        const dateObj = normalizeDateInput(formData.dueDate);
        if (!dateObj) {
          throw new Error(t('agtfin.bills.messages.invalidDate'));
        }
        formattedDate = formatLocalDateString(dateObj);
      } catch (dateError) {
        setFormErrors({ dueDate: t('agtfin.bills.messages.invalidDateFormat') });
        return;
      }
      
      const updateData = {
        name: formData.name.trim(),
        amount: parseFloat(formData.amount),
        dueDate: formattedDate,
        billType: formData.billType,
        category: formData.category?.trim() || '',
        description: formData.description?.trim() || '',
        isRecurring: Boolean(formData.isRecurring),
        frequency: formData.frequency
      };
      
      console.log('🔄 Actualizando factura:', selectedBill.billId, updateData);
      
      await agtFinService.updateBill(selectedBill.billId, updateData);
      
      // Recargar datos
      await loadBillsData();
      
      // Cerrar modal
      setOpenEditModal(false);
      setSelectedBill(null);
      
      console.log('✅ Factura actualizada exitosamente');
      
    } catch (err) {
      console.error('❌ Error actualizando factura:', err);
      
      let errorMessage = 'No se pudo actualizar la cuenta.';
      
      if (err.response?.status === 400) {
        errorMessage = err.response.data?.error || 'Datos inválidos. Verifica la información.';
      } else if (err.response?.status === 401) {
        errorMessage = 'Sesión expirada. Por favor, inicia sesión nuevamente.';
      } else if (err.userMessage) {
        errorMessage = err.userMessage;
      }
      
      setError(errorMessage);
    } finally {
      setSubmitLoading(false);
    }
  };

  // Marcar como pagada
  const handleMarkAsPaid = async (billId) => {
    try {
      setLoading(true);
      setError('');
      
      console.log('✅ Marcando factura como pagada:', billId);
      
      await agtFinService.markBillAsPaid(billId);
      
      // Recargar datos
      await loadBillsData();
      
      console.log('✅ Factura marcada como pagada exitosamente');
      
    } catch (err) {
      console.error('❌ Error marcando factura como pagada:', err);
      
      let errorMessage = 'No se pudo marcar la cuenta como pagada.';
      
      if (err.response?.status === 404) {
        errorMessage = 'La cuenta no existe o ya fue eliminada.';
      } else if (err.response?.status === 401) {
        errorMessage = 'Sesión expirada. Por favor, inicia sesión nuevamente.';
      } else if (err.userMessage) {
        errorMessage = err.userMessage;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Eliminar factura
  // Eliminar factura (continuación)
  const handleDeleteBill = async (billId) => {
    if (!window.confirm(t('agtfin.bills.actions.confirmDelete'))) {
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      console.log('🗑️ Eliminando factura:', billId);
      
      await agtFinService.deleteBill(billId);
      
      // Recargar datos
      await loadBillsData();
      
      console.log('✅ Factura eliminada exitosamente');
      
    } catch (err) {
      console.error('❌ Error eliminando factura:', err);
      
      let errorMessage = 'No se pudo eliminar la cuenta.';
      
      if (err.response?.status === 404) {
        errorMessage = 'La cuenta no existe o ya fue eliminada.';
      } else if (err.response?.status === 401) {
        errorMessage = 'Sesión expirada. Por favor, inicia sesión nuevamente.';
      } else if (err.userMessage) {
        errorMessage = err.userMessage;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Resetear formulario
  const resetForm = () => {
    setFormData({
      name: '',
      amount: '',
      dueDate: new Date(),
      billType: 'utilities',
      category: '',
      description: '',
      isRecurring: false,
      frequency: 'monthly'
    });
    setFormErrors({});
  };

  // Abrir modal de edición
  const openEditBill = (bill) => {
    setSelectedBill(bill);
    setFormData({
      name: bill.name,
      amount: bill.amount.toString(),
      dueDate: parseLocalDate(bill.dueDate) || new Date(),
      billType: bill.billType,
      category: bill.category || '',
      description: bill.description || '',
      isRecurring: bill.isRecurring || false,
      frequency: bill.frequency || 'monthly'
    });
    setFormErrors({});
    setOpenEditModal(true);
  };

  // Abrir modal de detalles
  const openBillDetails = (bill) => {
    setSelectedBill(bill);
    setOpenDetailsModal(true);
  };

  // Manejar cambios en el formulario
  const handleFormChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Limpiar error del campo si existe
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  // Manejar cambio de fecha
  const handleDateChange = (newDate) => {
    setFormData(prev => ({
      ...prev,
      dueDate: newDate
    }));
    
    if (formErrors.dueDate) {
      setFormErrors(prev => ({ ...prev, dueDate: undefined }));
    }
  };

  // Filtrar facturas
  const filteredBills = bills.filter(bill => {
    if (filterStatus !== 'all' && bill.status !== filterStatus) {
      return false;
    }
    
    if (showOverdueOnly) {
      const today = formatLocalDateString(new Date());
      return bill.status === 'pending' && bill.dueDate < today;
    }
    
    return true;
  });

  // Obtener color de estado
  const getStatusColor = (bill) => {
    const today = formatLocalDateString(new Date());
    
    if (bill.status === 'paid') return 'paid';
    if (bill.status === 'pending' && bill.dueDate < today) return 'overdue';
    return 'pending';
  };

  // Obtener texto de estado
  const getStatusText = (bill) => {
    const today = formatLocalDateString(new Date());

    if (bill.status === 'paid') return t('agtfin.bills.paid');
    if (bill.status === 'pending' && bill.dueDate < today) return t('agtfin.bills.overdue');
    return t('agtfin.bills.pending');
  };

  // Formatear moneda
  const formatCurrency = (amount) => {
    return agtFinService.formatCurrency(amount || 0, userProfile?.currency || 'USD');
  };

  // Obtener ícono según tipo de factura
  const getBillTypeIcon = (type) => {
    const billType = billTypes.find(bt => bt.value === type);
    return billType ? billType.icon : <ReceiptIcon />;
  };

  // Obtener nombre según tipo de factura
  const getBillTypeName = (type) => {
    const billType = billTypes.find(bt => bt.value === type);
    return billType ? billType.label : 'Otro';
  };

  // Mostrar spinner mientras carga
  if (loading && bills.length === 0) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '60vh',
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress size={48} sx={{ color: colors.primary }} />
        <Typography variant="body1" color="text.secondary">
          {t('agtfin.bills.loading')}
        </Typography>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
      <ModernContainer
        component={motion.div}
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Encabezado moderno */}
        <Box
          component={motion.div}
          variants={itemVariants}
          sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 4,
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 2 : 0
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              p: 1.5,
              borderRadius: '12px',
              background: colors.gradients.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <AnalyticsIcon sx={{ 
                fontSize: 24, 
                color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF' 
              }} />
            </Box>
            <Box>
              <Typography variant="h4" component="h1" fontWeight={700} sx={{ color: colors.primary }}>
                {t('agtfin.bills.title')}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary" fontWeight={500}>
                {t('agtfin.bills.subtitle')}
              </Typography>
            </Box>
          </Box>
          <ModernButton 
            startIcon={<AddIcon />}
            onClick={() => setOpenCreateModal(true)}
            disabled={submitLoading}
          >
            {t('agtfin.bills.new')}
          </ModernButton>
        </Box>

        {/* Mensaje de error */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3,
              borderRadius: 2,
              fontWeight: 500
            }}
            onClose={() => setError('')}
          >
            {error}
          </Alert>
        )}

        {/* Sección de resumen */}
        {summary && (
          <Box component={motion.div} variants={itemVariants}>
            <SummarySection
              summary={summary}
              userProfile={userProfile}
              theme={theme}
            />
          </Box>
        )}

        {/* Filtros modernos */}
        <Box
          component={motion.div}
          variants={itemVariants}
          sx={{ mb: 4 }}
        >
          <Typography variant="h5" fontWeight={700} sx={{ mb: 3, color: colors.primary }}>
            {t('agtfin.bills.filters.title')}
          </Typography>
          
          <Box sx={{ 
            p: 3, 
            borderRadius: 4, 
            background: colors.backgrounds.card,
            border: theme.palette.mode === 'dark' 
              ? '1px solid rgba(255,255,255,0.1)' 
              : '1px solid rgba(0,0,0,0.05)',
            boxShadow: theme.palette.mode === 'dark'
              ? '0 8px 32px rgba(0,0,0,0.2)'
              : '0 8px 32px rgba(0,0,0,0.08)',
            display: 'flex', 
            gap: 3, 
            flexWrap: 'wrap',
            alignItems: 'center'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterListIcon sx={{ color: colors.primary, fontSize: 20 }} />
              <Typography variant="subtitle2" fontWeight={600} sx={{ color: colors.primary }}>
                {t('agtfin.bills.filters.filterBy')}
              </Typography>
            </Box>

            <ModernFormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>{t('agtfin.bills.filters.status')}</InputLabel>
              <Select
                value={filterStatus}
                label={t('agtfin.bills.filters.status')}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="all">{t('agtfin.bills.filters.all')}</MenuItem>
                <MenuItem value="pending">{t('agtfin.bills.pending')}</MenuItem>
                <MenuItem value="paid">{t('agtfin.bills.paid')}</MenuItem>
                <MenuItem value="overdue">{t('agtfin.bills.overdue')}</MenuItem>
              </Select>
            </ModernFormControl>
            
            <FormControlLabel
              control={
                <Switch
                  checked={showOverdueOnly}
                  onChange={(e) => setShowOverdueOnly(e.target.checked)}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: colors.primary,
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: colors.secondary,
                    },
                  }}
                />
              }
              label={
                <Typography variant="body2" fontWeight={500}>
                  {t('agtfin.bills.filters.showOverdue')}
                </Typography>
              }
            />
          </Box>
        </Box>

        {/* Lista de facturas */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 3, color: colors.primary }}>
            {t('agtfin.bills.list')}
          </Typography>
          
          {filteredBills.length === 0 ? (
            <Box sx={{ 
              textAlign: 'center', 
              py: 8,
              background: colors.backgrounds.card,
              borderRadius: 4,
              border: theme.palette.mode === 'dark' 
                ? '1px solid rgba(255,255,255,0.1)' 
                : '1px solid rgba(0,0,0,0.05)',
            }}>
              <Box sx={{
                p: 3,
                borderRadius: '50%',
                background: colors.backgrounds.light,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3
              }}>
                <ReceiptIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.5 }} />
              </Box>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                {filterStatus !== 'all' || showOverdueOnly
                  ? t('agtfin.bills.empty.noResults')
                  : t('agtfin.bills.empty.noBills')
                }
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {filterStatus === 'all' && !showOverdueOnly
                  ? t('agtfin.bills.empty.noBillsHelp')
                  : t('agtfin.bills.empty.noResultsHelp')
                }
              </Typography>
              {filterStatus === 'all' && !showOverdueOnly && (
                <ModernButton
                  startIcon={<AddIcon />}
                  onClick={() => setOpenCreateModal(true)}
                >
                  {t('agtfin.bills.empty.addFirst')}
                </ModernButton>
              )}
            </Box>
          ) : (
            <Box>
              {filteredBills.map((bill) => {
                const status = getStatusColor(bill);
                const statusText = getStatusText(bill);
                
                return (
                  <ModernBillCard key={bill.billId} status={status}>
                    <CardContent sx={{ p: 4 }}>
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'flex-start',
                        mb: 3
                      }}>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Box sx={{
                              p: 1.5,
                              borderRadius: '12px',
                              background: colors.backgrounds.medium,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              {React.cloneElement(getBillTypeIcon(bill.billType), { 
                                sx: { fontSize: 20, color: colors.primary } 
                              })}
                            </Box>
                            <Box>
                              <Typography variant="h6" fontWeight={600} sx={{ color: colors.primary }}>
                                {bill.name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                                {getBillTypeName(bill.billType)}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                        
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="h5" fontWeight={700} sx={{ color: colors.primary, mb: 1 }}>
                            {formatCurrency(bill.amount)}
                          </Typography>
                          <ModernChip 
                            label={statusText} 
                            chiptype={status}
                            size="small"
                          />
                        </Box>
                      </Box>
                      
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 3, 
                        mb: 3,
                        flexWrap: 'wrap'
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary" fontWeight={500}>
                            {t('agtfin.bills.details.dueLabel')} {format(parseLocalDate(bill.dueDate) || new Date(), 'dd/MM/yyyy')}
                          </Typography>
                        </Box>
                        
                        {bill.isRecurring && (
                          <ModernChip
                            label={t('agtfin.bills.recurringFormat', {
                              frequency:
                                bill.frequency === 'weekly' ? t('agtfin.bills.form.weekly') :
                                bill.frequency === 'monthly' ? t('agtfin.bills.form.monthly') :
                                bill.frequency === 'quarterly' ? t('agtfin.bills.form.quarterly') :
                                bill.frequency === 'yearly' ? t('agtfin.bills.form.yearly') :
                                bill.frequency
                            })}
                            size="small"
                            variant="outlined"
                            sx={{
                              borderColor: colors.backgrounds.dark,
                              color: colors.secondary,
                              background: 'transparent'
                            }}
                          />
                        )}
                        
                        {bill.category && (
                          <ModernChip 
                            label={bill.category}
                            size="small"
                            variant="outlined"
                            sx={{ 
                              borderColor: colors.backgrounds.dark,
                              color: colors.secondary,
                              background: 'transparent'
                            }}
                          />
                        )}
                      </Box>
                      
                      {bill.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontStyle: 'italic' }}>
                          {bill.description}
                        </Typography>
                      )}
                      
                      <Divider sx={{ my: 3, backgroundColor: colors.backgrounds.medium }} />
                      
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Tooltip title={t('agtfin.bills.actions.viewDetails')} arrow>
                          <IconButton
                            size="small"
                            onClick={() => openBillDetails(bill)}
                            sx={{
                              borderRadius: 2,
                              background: colors.backgrounds.light,
                              '&:hover': {
                                background: colors.backgrounds.medium,
                                transform: 'scale(1.1)'
                              },
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <VisibilityIcon fontSize="small" sx={{ color: colors.primary }} />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title={t('agtfin.bills.actions.edit')} arrow>
                          <IconButton
                            size="small"
                            onClick={() => openEditBill(bill)}
                            disabled={submitLoading}
                            sx={{
                              borderRadius: 2,
                              background: colors.backgrounds.light,
                              '&:hover': {
                                background: colors.backgrounds.medium,
                                transform: 'scale(1.1)'
                              },
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <EditIcon fontSize="small" sx={{ color: colors.primary }} />
                          </IconButton>
                        </Tooltip>
                        
                        {status !== 'paid' && (
                          <Tooltip title={t('agtfin.bills.actions.markPaid')} arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleMarkAsPaid(bill.billId)}
                              disabled={loading}
                              sx={{
                                borderRadius: 2,
                                background: colors.backgrounds.light,
                                '&:hover': {
                                  background: colors.backgrounds.medium,
                                  transform: 'scale(1.1)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <CheckIcon fontSize="small" sx={{ color: colors.primary }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        
                        <Tooltip title={t('agtfin.bills.actions.delete')} arrow>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteBill(bill.billId)}
                            disabled={loading}
                            sx={{
                              borderRadius: 2,
                              background: colors.backgrounds.light,
                              '&:hover': {
                                background: colors.backgrounds.medium,
                                transform: 'scale(1.1)'
                              },
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <DeleteIcon fontSize="small" sx={{ color: colors.tertiary }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </CardContent>
                  </ModernBillCard>
                );
              })}
            </Box>
          )}
        </Box>

        {/* Modal de crear factura */}
        <ModernDialog 
          open={openCreateModal} 
          onClose={() => setOpenCreateModal(false)}
          maxWidth="sm"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle sx={{ 
            pb: 2, 
            borderBottom: `1px solid ${colors.backgrounds.medium}`,
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}>
            <Box sx={{
              p: 1,
              borderRadius: '8px',
              background: colors.gradients.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <ReceiptIcon sx={{ 
                fontSize: 20, 
                color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF' 
              }} />
            </Box>
            <Typography variant="h6" fontWeight={600}>
              {t('agtfin.bills.new')}
            </Typography>
          </DialogTitle>
          
          <DialogContent sx={{ pt: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <ModernTextField
                  fullWidth
                  label={t('agtfin.bills.form.name')}
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                  error={!!formErrors.name}
                  helperText={formErrors.name}
                  placeholder={t('agtfin.bills.form.namePlaceholder')}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <ModernTextField
                  fullWidth
                  label={t('agtfin.bills.form.amount')}
                  name="amount"
                  type="number"
                  value={formData.amount}
                  onChange={handleFormChange}
                  error={!!formErrors.amount}
                  helperText={formErrors.amount}
                  InputProps={{
                    startAdornment: (
                      <Typography 
                        variant="body1" 
                        color="text.secondary" 
                        sx={{ mr: 1, fontWeight: 600 }}
                      >
                        {userProfile?.currency === 'USD' ? '$' : 
                         userProfile?.currency === 'EUR' ? '€' : 
                         userProfile?.currency === 'CLP' ? '$' : 
                         userProfile?.currency || '$'}
                      </Typography>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                  <DatePicker
                    label={t('agtfin.bills.form.dueDate')}
                    value={formData.dueDate}
                    onChange={handleDateChange}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!formErrors.dueDate,
                        helperText: formErrors.dueDate,
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                            background: colors.backgrounds.light,
                          }
                        }
                      }
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <ModernFormControl fullWidth>
                  <InputLabel>{t('agtfin.bills.form.billType')}</InputLabel>
                  <Select
                    name="billType"
                    value={formData.billType}
                    label={t('agtfin.bills.form.billType')}
                    onChange={handleFormChange}
                  >
                    {billTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {React.cloneElement(type.icon, { fontSize: 'small' })}
                          {type.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </ModernFormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <ModernTextField
                  fullWidth
                  label={t('agtfin.bills.form.category')}
                  name="category"
                  value={formData.category}
                  onChange={handleFormChange}
                  placeholder={t('agtfin.bills.form.categoryPlaceholder')}
                />
              </Grid>
              
              <Grid item xs={12}>
                <ModernTextField
                  fullWidth
                  label={t('agtfin.bills.form.description')}
                  name="description"
                  value={formData.description}
                  onChange={handleFormChange}
                  multiline
                  rows={3}
                  placeholder={t('agtfin.bills.form.descriptionPlaceholder')}
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      name="isRecurring"
                      checked={formData.isRecurring}
                      onChange={handleFormChange}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: colors.primary,
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: colors.secondary,
                        },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body1" fontWeight={500}>
                      {t('agtfin.bills.form.recurring')}
                    </Typography>
                  }
                />
              </Grid>
              
              {formData.isRecurring && (
                <Grid item xs={12}>
                  <ModernFormControl fullWidth>
                    <InputLabel>{t('agtfin.bills.form.frequency')}</InputLabel>
                    <Select
                      name="frequency"
                      value={formData.frequency}
                      label={t('agtfin.bills.form.frequency')}
                      onChange={handleFormChange}
                    >
                      <MenuItem value="weekly">{t('agtfin.bills.form.weekly')}</MenuItem>
                      <MenuItem value="monthly">{t('agtfin.bills.form.monthly')}</MenuItem>
                      <MenuItem value="quarterly">{t('agtfin.bills.form.quarterly')}</MenuItem>
                      <MenuItem value="yearly">{t('agtfin.bills.form.yearly')}</MenuItem>
                    </Select>
                  </ModernFormControl>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          
          <DialogActions sx={{ 
            p: 3, 
            borderTop: `1px solid ${colors.backgrounds.medium}`,
            gap: 2
          }}>
            <Button 
              onClick={() => setOpenCreateModal(false)}
              disabled={submitLoading}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                color: colors.secondary,
                '&:hover': {
                  background: colors.backgrounds.light
                }
              }}
            >
              {t('agtfin.common.cancel')}
            </Button>
            <ModernButton 
              onClick={handleCreateBill}
              disabled={!formData.name || !formData.amount || submitLoading}
              startIcon={submitLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <AddIcon fontSize="small" />
              )}
            >
              {submitLoading ? t('agtfin.bills.form.saving') : t('agtfin.bills.form.create')}
            </ModernButton>
          </DialogActions>
        </ModernDialog>

        {/* Modal de editar factura */}
        <ModernDialog 
          open={openEditModal} 
          onClose={() => setOpenEditModal(false)}
          maxWidth="sm"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle sx={{ 
            pb: 2, 
            borderBottom: `1px solid ${colors.backgrounds.medium}`,
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}>
            <Box sx={{
              p: 1,
              borderRadius: '8px',
              background: colors.gradients.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <EditIcon sx={{ 
                fontSize: 20, 
                color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF' 
              }} />
            </Box>
            <Typography variant="h6" fontWeight={600}>
              {t('agtfin.bills.form.edit')}
            </Typography>
          </DialogTitle>

          <DialogContent sx={{ pt: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <ModernTextField
                  fullWidth
                  label={t('agtfin.bills.form.name')}
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                  error={!!formErrors.name}
                  helperText={formErrors.name}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <ModernTextField
                  fullWidth
                  label={t('agtfin.bills.form.amount')}
                  name="amount"
                  type="number"
                  value={formData.amount}
                  onChange={handleFormChange}
                  error={!!formErrors.amount}
                  helperText={formErrors.amount}
                  InputProps={{
                    startAdornment: (
                      <Typography 
                        variant="body1" 
                        color="text.secondary" 
                        sx={{ mr: 1, fontWeight: 600 }}
                      >
                        {userProfile?.currency === 'USD' ? '$' : 
                         userProfile?.currency === 'EUR' ? '€' : 
                         userProfile?.currency === 'CLP' ? '$' : 
                         userProfile?.currency || '$'}
                      </Typography>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                  <DatePicker
                    label={t('agtfin.bills.form.dueDate')}
                    value={formData.dueDate}
                    onChange={handleDateChange}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!formErrors.dueDate,
                        helperText: formErrors.dueDate,
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                            background: colors.backgrounds.light,
                          }
                        }
                      }
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <ModernFormControl fullWidth>
                  <InputLabel>{t('agtfin.bills.form.billType')}</InputLabel>
                  <Select
                    name="billType"
                    value={formData.billType}
                    label={t('agtfin.bills.form.billType')}
                    onChange={handleFormChange}
                  >
                    {billTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {React.cloneElement(type.icon, { fontSize: 'small' })}
                          {type.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </ModernFormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <ModernTextField
                  fullWidth
                  label={t('agtfin.bills.form.category')}
                  name="category"
                  value={formData.category}
                  onChange={handleFormChange}
                  placeholder={t('agtfin.bills.form.categoryPlaceholder')}
                />
              </Grid>
              
              <Grid item xs={12}>
                <ModernTextField
                  fullWidth
                  label={t('agtfin.bills.form.description')}
                  name="description"
                  value={formData.description}
                  onChange={handleFormChange}
                  multiline
                  rows={3}
                  placeholder={t('agtfin.bills.form.descriptionPlaceholder')}
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      name="isRecurring"
                      checked={formData.isRecurring}
                      onChange={handleFormChange}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: colors.primary,
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: colors.secondary,
                        },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body1" fontWeight={500}>
                      {t('agtfin.bills.form.recurring')}
                    </Typography>
                  }
                />
              </Grid>
              
              {formData.isRecurring && (
                <Grid item xs={12}>
                  <ModernFormControl fullWidth>
                    <InputLabel>{t('agtfin.bills.form.frequency')}</InputLabel>
                    <Select
                      name="frequency"
                      value={formData.frequency}
                      label={t('agtfin.bills.form.frequency')}
                      onChange={handleFormChange}
                    >
                      <MenuItem value="weekly">{t('agtfin.bills.form.weekly')}</MenuItem>
                      <MenuItem value="monthly">{t('agtfin.bills.form.monthly')}</MenuItem>
                      <MenuItem value="quarterly">{t('agtfin.bills.form.quarterly')}</MenuItem>
                      <MenuItem value="yearly">{t('agtfin.bills.form.yearly')}</MenuItem>
                    </Select>
                  </ModernFormControl>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          
          <DialogActions sx={{ 
            p: 3, 
            borderTop: `1px solid ${colors.backgrounds.medium}`,
            gap: 2
          }}>
            <Button 
              onClick={() => setOpenEditModal(false)}
              disabled={submitLoading}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                color: colors.secondary,
                '&:hover': {
                  background: colors.backgrounds.light
                }
              }}
            >
              {t('agtfin.common.cancel')}
            </Button>
            <ModernButton 
              onClick={handleUpdateBill}
              disabled={!formData.name || !formData.amount || submitLoading}
              startIcon={submitLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <EditIcon fontSize="small" />
              )}
            >
              {submitLoading ? t('agtfin.bills.form.updating') : t('agtfin.bills.form.update')}
            </ModernButton>
          </DialogActions>
        </ModernDialog>

        {/* Modal de detalles de factura */}
        <ModernDialog 
          open={openDetailsModal} 
          onClose={() => setOpenDetailsModal(false)}
          maxWidth="md"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle sx={{ 
            pb: 2, 
            borderBottom: `1px solid ${colors.backgrounds.medium}`,
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}>
            <Box sx={{
              p: 1,
              borderRadius: '8px',
              background: colors.gradients.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <VisibilityIcon sx={{ 
                fontSize: 20, 
                color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF' 
              }} />
            </Box>
            <Typography variant="h6" fontWeight={600}>
              {t('agtfin.bills.details.title')}
            </Typography>
          </DialogTitle>

          <DialogContent sx={{ pt: 3 }}>
            {selectedBill && (
              <Grid container spacing={4}>
                {/* Información principal */}
                <Grid item xs={12} md={8}>
                  <Paper sx={{ 
                    p: 4, 
                    borderRadius: 4,
                    background: colors.backgrounds.card,
                    border: theme.palette.mode === 'dark' 
                      ? '1px solid rgba(255,255,255,0.1)' 
                      : '1px solid rgba(0,0,0,0.05)',
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 4 }}>
                      <Box sx={{
                        p: 2,
                        borderRadius: '16px',
                        background: colors.gradients.primary,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {React.cloneElement(getBillTypeIcon(selectedBill.billType), { 
                          sx: { 
                            fontSize: 32, 
                            color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF' 
                          } 
                        })}
                      </Box>
                      <Box>
                        <Typography variant="h4" fontWeight={700} sx={{ color: colors.primary, mb: 1 }}>
                          {selectedBill.name}
                        </Typography>
                        <Typography variant="h5" fontWeight={600} sx={{ color: colors.secondary }}>
                          {formatCurrency(selectedBill.amount)}
                        </Typography>
                      </Box>
                    </Box>

                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
                            {t('agtfin.bills.details.type')}
                          </Typography>
                          <Typography variant="body1" fontWeight={500} sx={{ color: colors.primary }}>
                            {getBillTypeName(selectedBill.billType)}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
                            {t('agtfin.bills.details.status')}
                          </Typography>
                          <ModernChip 
                            label={getStatusText(selectedBill)} 
                            chiptype={getStatusColor(selectedBill)}
                            size="small"
                          />
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
                            {t('agtfin.bills.details.dueDate')}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body1" fontWeight={500} sx={{ color: colors.primary }}>
                              {format(parseLocalDate(selectedBill.dueDate) || new Date(), 'dd/MM/yyyy')}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
                            {t('agtfin.bills.details.createdAt')}
                          </Typography>
                          <Typography variant="body1" fontWeight={500} sx={{ color: colors.primary }}>
                            {selectedBill.createdAt ? format(new Date(selectedBill.createdAt), 'dd/MM/yyyy HH:mm') : t('agtfin.common.notAvailable')}
                          </Typography>
                        </Box>
                      </Grid>

                      {selectedBill.category && (
                        <Grid item xs={12}>
                          <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
                              {t('agtfin.bills.details.category')}
                            </Typography>
                            <ModernChip 
                              label={selectedBill.category}
                              size="small"
                              variant="outlined"
                              sx={{ 
                                borderColor: colors.backgrounds.dark,
                                color: colors.secondary,
                                background: 'transparent'
                              }}
                            />
                          </Box>
                        </Grid>
                      )}

                      {selectedBill.description && (
                        <Grid item xs={12}>
                          <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
                              {t('agtfin.bills.details.description')}
                            </Typography>
                            <Typography variant="body1" sx={{ 
                              color: colors.primary,
                              fontStyle: 'italic',
                              background: colors.backgrounds.light,
                              p: 2,
                              borderRadius: 2,
                              border: `1px solid ${colors.backgrounds.medium}`
                            }}>
                              {selectedBill.description}
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </Paper>
                </Grid>

                {/* Panel lateral con información adicional */}
                <Grid item xs={12} md={4}>
                  <Paper sx={{ 
                    p: 3, 
                    borderRadius: 4,
                    background: colors.backgrounds.card,
                    border: theme.palette.mode === 'dark' 
                      ? '1px solid rgba(255,255,255,0.1)' 
                      : '1px solid rgba(0,0,0,0.05)',
                    height: 'fit-content'
                  }}>
                    <Typography variant="h6" fontWeight={600} sx={{ mb: 3, color: colors.primary }}>
                      {t('agtfin.bills.details.additionalInfo')}
                    </Typography>

                    {/* Información de recurrencia */}
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ mb: 2 }}>
                        {t('agtfin.bills.details.recurrence')}
                      </Typography>
                      {selectedBill.isRecurring ? (
                        <Box>
                          <ModernChip 
                            label={t('agtfin.bills.details.recurring')}
                            chiptype="paid"
                            size="small"
                            sx={{ mb: 2 }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            {t('agtfin.bills.details.frequency')} <strong>{
                              selectedBill.frequency === 'weekly' ? t('agtfin.bills.form.weekly') :
                              selectedBill.frequency === 'monthly' ? t('agtfin.bills.form.monthly') :
                              selectedBill.frequency === 'quarterly' ? t('agtfin.bills.form.quarterly') :
                              selectedBill.frequency === 'yearly' ? t('agtfin.bills.form.yearly') :
                              selectedBill.frequency
                            }</strong>
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {t('agtfin.bills.details.nonRecurring')}
                        </Typography>
                      )}
                    </Box>

                    {/* Días hasta vencimiento */}
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
                        {t('agtfin.bills.details.daysUntilDue')}
                      </Typography>
                      {(() => {
                        const today = new Date();
                        const dueDate = parseLocalDate(selectedBill.dueDate) || new Date();
                        const diffTime = dueDate - today;
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                        
                        let color = colors.primary;
                        let icon = <AccessTimeIcon sx={{ fontSize: 16 }} />;
                        let text = '';
                        
                        if (selectedBill.status === 'paid') {
                          color = colors.status.paid;
                          icon = <CheckIcon sx={{ fontSize: 16 }} />;
                          text = t('agtfin.bills.paid');
                        } else if (diffDays < 0) {
                          color = colors.status.overdue;
                          icon = <WarningIcon sx={{ fontSize: 16 }} />;
                          text = t('agtfin.bills.details.overdueDays', { days: Math.abs(diffDays) });
                        } else if (diffDays === 0) {
                          color = colors.status.overdue;
                          icon = <WarningIcon sx={{ fontSize: 16 }} />;
                          text = t('agtfin.bills.details.dueToday');
                        } else if (diffDays <= 7) {
                          color = colors.status.pending;
                          icon = <AccessTimeIcon sx={{ fontSize: 16 }} />;
                          text = t('agtfin.bills.details.dueDays', { days: diffDays });
                        } else {
                          color = colors.status.pending;
                          icon = <AccessTimeIcon sx={{ fontSize: 16 }} />;
                          text = t('agtfin.bills.details.dueDays', { days: diffDays });
                        }
                        
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {React.cloneElement(icon, { sx: { color } })}
                            <Typography variant="body1" fontWeight={500} sx={{ color }}>
                              {text}
                            </Typography>
                          </Box>
                        );
                      })()}
                    </Box>

                    {/* Acciones rápidas */}
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ mb: 2 }}>
                        {t('agtfin.bills.details.quickActions')}
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {getStatusColor(selectedBill) !== 'paid' && (
                          <ModernButton
                            fullWidth
                            startIcon={<CheckIcon />}
                            onClick={() => {
                              handleMarkAsPaid(selectedBill.billId);
                              setOpenDetailsModal(false);
                            }}
                            disabled={loading}
                            size="small"
                          >
                            {t('agtfin.bills.details.markPaid')}
                          </ModernButton>
                        )}
                        
                        <Button
                          fullWidth
                          variant="outlined"
                          startIcon={<EditIcon />}
                          onClick={() => {
                            setOpenDetailsModal(false);
                            openEditBill(selectedBill);
                          }}
                          disabled={loading}
                          sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 500,
                            borderColor: colors.backgrounds.dark,
                            color: colors.primary,
                            '&:hover': {
                              borderColor: colors.primary,
                              background: colors.backgrounds.light
                            }
                          }}
                          size="small"
                        >
                          {t('agtfin.bills.details.editBill')}
                        </Button>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          
          <DialogActions sx={{ 
            p: 3, 
            borderTop: `1px solid ${colors.backgrounds.medium}`,
            justifyContent: 'flex-end'
          }}>
            <Button 
              onClick={() => setOpenDetailsModal(false)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                color: colors.secondary,
                '&:hover': {
                  background: colors.backgrounds.light
                }
              }}
            >
              {t('agtfin.bills.details.close')}
            </Button>
          </DialogActions>
        </ModernDialog>
      </ModernContainer>
    </LocalizationProvider>
  );
};

export default FinBill;
